import {ActionIcon, Badge} from '@mantine/core';
import {Table} from 'src/design/atoms/Table';
import {Group} from 'src/design/atoms/Group';
import {ScrollArea} from 'src/design/atoms/ScrollArea';
import {Text} from 'src/design/atoms/Text';
import {Tooltip} from 'src/design/atoms/Tooltip';
import {IconRefresh, IconTrash} from '@tabler/icons-react';
import React from 'react';
import {deleteItem, retryItem} from 'src/offline/outbox';
import type {OutboxItem, OutboxItemStatus} from 'src/offline/types';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

const STATUS_COLOR: Record<OutboxItemStatus, string> = {
  pending: 'gray',
  syncing: 'blue',
  succeeded: 'green',
  failed: 'red',
};

export interface OutboxTableProps {
  items: OutboxItem[];
}

export const OutboxTable: React.FC<OutboxTableProps> = ({items}) => {
  const {setAlert} = useAlert();

  const handleRetry = async (id: string) => {
    try {
      await retryItem(id);
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteItem(id);
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  if (items.length === 0) {
    return (
      <Text size="sm" c="dimmed" p="md">
        No queued writes yet.
      </Text>
    );
  }

  return (
    <ScrollArea>
      <Table striped highlightOnHover withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Seq</Table.Th>
            <Table.Th>Kind</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Summary / Error</Table.Th>
            <Table.Th>Created</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map(item => (
            <Table.Tr key={item.id}>
              <Table.Td>{item.seq}</Table.Td>
              <Table.Td>{item.kind}</Table.Td>
              <Table.Td>
                <Badge color={STATUS_COLOR[item.status]} variant="light">
                  {item.status}
                </Badge>
              </Table.Td>
              <Table.Td>
                <Text size="sm" c={item.status === 'failed' ? 'red' : undefined}>
                  {item.error ?? item.resultSummary ?? '—'}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm">
                  {new Date(item.createdAt).toLocaleString()}
                </Text>
              </Table.Td>
              <Table.Td>
                <Group gap={4} wrap="nowrap" justify="flex-end">
                  {item.status === 'failed' && (
                    <Tooltip label="Retry" position="top" withArrow>
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        onClick={() => handleRetry(item.id)}>
                        <IconRefresh size={14} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  {(item.status === 'pending' || item.status === 'failed') && (
                    <Tooltip label="Delete" position="top" withArrow>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="sm"
                        onClick={() => handleDelete(item.id)}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </ScrollArea>
  );
};
